import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { Data } from 'core/utilities';
import { useKeypress } from 'view/hooks';
import { Spinner } from 'view/components/layout';
import { 
    Card, 
    CardBody, 
    CardHeader,
    CardFooter,
    CardError,
    Button,
    Toggle,
    OptionSelect,
    TextInput,
    MaskedInput,
    LocationInput,
    TypeChoice,
    IncomeInput
} from 'view/components/common';
import { IProgressContext, ProgressContext } from 'provider/ProgressContext';

export enum InputType
{
    info     = 'info',
    text     = 'text',
    email    = 'email',
    password = 'password',
    location = 'location',
    toggle   = 'toggle',
    select   = 'select',
    choice   = 'choice',
    income   = 'income'
}
export type FormInput = {
    type          : InputType;
    name          : string;
    label?        : string;
    placeholder?  : string;
    validator?    : Validator;
    normalizer?   : (e : React.ChangeEvent<HTMLInputElement>) => void;
    initialValue? : any;
    options?      : Array<{ value : string; label : string; }>;
    Component?    : React.FunctionComponent<{ onContinue : () => void; }>;
    showTerms?    : boolean;
}
export type FormStep = Array<FormInput>;
export type SetSubmitError = (error? : string) => void;
export type SetIsLoading = (loading : boolean) => void;
type Validator = (value : string) => string;
type ValidationSchema = {
    [key : string] : Validator;
};

interface Props 
{
    // Card header props
    title      : string;
    backText?  : string;
    stepTitle? : (step : number) => string;
    onExit?    : () => void;

    // Form props
    initialValues : Data;
    formSteps     : Array<FormStep>;
    onSubmit?     : any;  // Submit handler
    onNext?       : Data; // { [key : string] : (values : Data, setLoading : SetIsLoading, setError : SetSubmitError) => void; }
    onBack?       : Data; 
    disabled?     : boolean;
}

export const MultistepForm : React.FunctionComponent<Props> = ({
    title,
    backText,
    stepTitle,
    onExit,
    initialValues,
    formSteps,
    onSubmit,
    onNext,
    onBack,
    children,
    disabled,
}) => {
    const [ currentStep, setCurrentStep ] = useState<number>(0);
    const [ submitError, setSubmitError ] = useState<string | undefined>();
    const [ isLoading,   setIsLoading   ] = useState<boolean>(false);
    const [ formValues,  setFormValues  ] = useState<Data>(initialValues);
    const [ canContinue, setCanContinue ] = useState<boolean>(false);
    const [ timeoutId, setTimeoutId ] = useState<any>(null);
    const { register, setValue, getValues, handleSubmit } = useForm({ mode : 'onChange' });
    const { progressForward, progressBackward } = useContext(ProgressContext) as IProgressContext;

    const validate = (form : Data, schema : ValidationSchema) : boolean => {
        return Object.keys(form).reduce((valid : boolean, name : string) => {
            if (name in schema) {
                const value = form[name];
                const result = schema[name](value);
                return (result === '') && valid;
            }

            return valid;
        }, true) as boolean;
    };

    const numberOfSteps = formSteps.length - 1;
    const isFirstStep   = currentStep === 0;
    const isFinalStep   = currentStep === numberOfSteps;

    useEffect(() => { 
        // Scroll to top
        window.scrollTo(0, 0);

        // Refresh our copy of the form values on step change, to populate initial values
        setFormValues(getValues());
    }, [ currentStep, setFormValues, getValues ]);

    // Continuously revalidate the form values
    useEffect(() => {
        const validationSchema = formSteps[currentStep].reduce((validators, { name, validator }) => {
            if (validator) {
                return {
                    ...validators,
                    [name] : validator
                };
            }
            return validators;
        }, {}) as ValidationSchema;

        setCanContinue(validate(getValues(), validationSchema));
    }, [ formValues, currentStep, setCanContinue ]);

    const onPrevious = async () => {
        if (isFirstStep) {
            onExit && onExit();
        } else {
            let stepDecrement = 1;
            const back = onBack && onBack[currentStep];
            if (back) {
                const values = getValues();
                stepDecrement = await back(values) ?? 1;
            }

            setCurrentStep(s => s - stepDecrement);
            progressBackward();
            setCanContinue(true);
        }
    };

    const onContinue = async () => {
        if (!isFinalStep) {
            let stepIncrement = 1;
            const next = onNext && onNext[currentStep];
            // Intermediate submit hook
            if (next) {
                const values = getValues();
                stepIncrement = await next(values, setIsLoading, setSubmitError) ?? 1;
            }

            const timeoutId = setTimeout(() => {
                setCurrentStep(s => s + stepIncrement);
                progressForward();
                setIsLoading(false);
            }, 500);

            setTimeoutId(timeoutId);
        }
    };

    useEffect(() => {
        return(() => clearTimeout(timeoutId));
    }, []);

    const onSubmitForm = (e : React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        isFinalStep && handleSubmit(onSubmit(setSubmitError, setIsLoading))(e);
    };

    useKeypress('All', () => {   // Update copy of values on each key event
        setFormValues(getValues());
    });

    // Trigger `onContinue` on enter keypress
    useKeypress('Enter', () => {
        if (canContinue && !isLoading) {
            onContinue();
        }
    });

    return (
        <FormWrapper onSubmit={onSubmitForm}>
            <Card height={600}>
                <CardHeader
                    content={title}
                    step={stepTitle && stepTitle(currentStep)}
                    backHandler={onPrevious}
                    backText={backText}
                />
                {submitError && (
                    <CardError
                        content={submitError}
                        css={'margin-bottom: -5px; margin-top: -10px;'}
                    />
                )}
                <CardBody>
                    {isLoading ? (
                        <Spinner css='height: inherit; margin-top: 100px;' />
                    ) : (
                        formSteps[currentStep].map((
                            { 
                                type, 
                                name, 
                                label,
                                placeholder,
                                validator,
                                normalizer,
                                options,
                                initialValue,
                                Component,
                                showTerms
                            } : FormInput, 
                            index : number) => {
                            const key = `step_${currentStep}_input_${index}`;
                            const value = initialValue ?? initialValues[name];
                            switch (type) {
                            case InputType.info:
                                return (
                                    Component && <Component key={key} onContinue={onContinue} />
                                );

                            case InputType.text:
                            case InputType.email:
                                return (
                                    <TextInput
                                        key={key}
                                        type={type.toString()}
                                        name={name}
                                        label={label}
                                        placeholder={placeholder}
                                        initialValue={value}
                                        validator={validator}
                                        normalizer={normalizer}
                                        setValue={setValue}
                                        input={register(name)}
                                    />
                                );
                                
                            case InputType.password:
                                return (
                                    <MaskedInput
                                        key={key}
                                        name={name}
                                        label={label}
                                        placeholder={placeholder}
                                        validator={validator}
                                        normalizer={normalizer}
                                        setValue={setValue}
                                        input={register(name)}
                                    />
                                );

                            case InputType.income:
                                return (
                                    <IncomeInput
                                        key={key}
                                        type={type.toString()}
                                        name={name}
                                        label={label}
                                        placeholder={placeholder}
                                        initialValue={value}
                                        validator={validator}
                                        normalizer={normalizer}
                                        setValue={setValue}
                                        input={register(name)}
                                    />
                                );
                                
                            case InputType.location: // FIXME only supports one location input per form :(
                                return (
                                    <LocationInput
                                        key={key}
                                        label={label}
                                        placeholder={placeholder}
                                        initialValues={{
                                            ...initialValues,
                                            ...formValues
                                        }}
                                        setValue={(name: string, value: any) => {   
                                            setValue(name, value);
                                            setFormValues(getValues());
                                        }}
                                        inputs={{
                                            full_address : register('full_address'),
                                            address      : register('address'),
                                            city         : register('city'),
                                            region       : register('region'),
                                            country      : register('country'),
                                            postal       : register('postal'),
                                        }}
                                    />
                                );

                            case InputType.toggle:
                                return (
                                    <Toggle
                                        key={key}
                                        name={name}
                                        label={label}
                                        initialValue={value}
                                        setValue={setValue}
                                        input={register(name)}
                                        showTerms={showTerms}
                                    />
                                );

                            case InputType.select:
                                return (
                                    <OptionSelect
                                        key={key}
                                        name={name}
                                        label={label}
                                        options={options ?? []}
                                        initialValue={value}
                                        setValue={setValue}
                                        input={register(name)}
                                    />
                                );

                            case InputType.choice:
                                return (
                                    <TypeChoice
                                        key={key}
                                        name={name}
                                        input={register(name)}
                                        initialValue={formValues[name]}
                                        setValue={(name: string, value: any) => {   
                                            setValue(name, value);
                                            setFormValues(getValues());
                                        }}
                                    />
                                );
                            }
                        })
                    )}
                </CardBody>
                <Spacing />
                <CardFooter>
                    <Button
                        title='CONTINUE'
                        type={isFinalStep ? 'submit' : 'button'}
                        onClick={onContinue}
                        disabled={!canContinue || isLoading || disabled}
                        css={'text-transform: none;'}
                        arrowCss={'padding-left: 0px; margin-top: 7px;'}
                    />
                    {children}
                </CardFooter>
            </Card>
        </FormWrapper>
    );
};

const FormWrapper = styled.form`
    width: 100%;
    height: calc(100vh - 200px);
`;

const Spacing = styled.div`
    height: 130px;
`;
